import { z } from "zod";

/**
 * Çok taraflı müzakere şemaları (CLOS-252 `[145]`, model: ADR-0034).
 *
 * Set = paylaşılan baz versiyona değişiklik öğeleri. Karar HEP karşı tarafta; kısmi
 * kabul öğe bazında ve toplam durumdan ayrı; karşı öneri yeni set (parent zinciri);
 * sessiz merge yok — uygulama açık kullanıcı komutuyla CLOS-134'ten.
 */

const ogeSemasi = z.strictObject({
  targetClauseId: z.string().trim().min(1, "Hedef madde zorunludur"),
  targetBlockIndex: z.number().int().min(0).optional(),
  currentText: z.string().min(1, "Mevcut metin zorunludur"),
  proposedText: z.string().min(1, "Önerilen metin zorunludur"),
  note: z.string().trim().max(1000).optional(),
});

export const createNegotiationSchema = z.strictObject({
  baseVersionId: z.uuid("Geçerli bir baz versiyon seçilmelidir"),
  /**
   * KAYNAK PAYLAŞIM — haricî/nesne paylaşımı yolunda ZORUNLU, Müvekkil Alanında YOK.
   *
   * ⚠ İki yetkilendirme kaynağı var ve biri diğerinin yerine geçmiyor: katılımcı
   * organizasyon bir NESNE PAYLAŞIMINA dayanarak öneri veriyor; Müvekkil Alanı temsilcisi
   * ise SÖZLEŞME DÜZEYİNDEKİ alan grant'ine dayanıyor. Alanı zorunlu tutmak, Müvekkil
   * Alanı için yanlış yetkilendirme kaynağını dayatıyordu.
   */
  sourceShareId: z.uuid("Geçerli bir kaynak paylaşım seçilmelidir").nullish(),
  note: z.string().trim().max(2000).optional(),
  items: z.array(ogeSemasi).min(1, "En az bir değişiklik öğesi gerekir").max(50),
});
export type CreateNegotiationInput = z.infer<typeof createNegotiationSchema>;

/** Karşı öneri — kaynak paylaşım parent'tan devralınır, öğeler yeni. */
export const counterNegotiationSchema = z.strictObject({
  note: z.string().trim().max(2000).optional(),
  items: z.array(ogeSemasi).min(1, "En az bir değişiklik öğesi gerekir").max(50),
});
export type CounterNegotiationInput = z.infer<typeof counterNegotiationSchema>;

export const decideNegotiationItemSchema = z.strictObject({
  decision: z.enum(["accept", "reject"], "Geçerli bir karar seçilmelidir"),
  note: z.string().trim().max(1000).optional(),
});
export type DecideNegotiationItemInput = z.infer<typeof decideNegotiationItemSchema>;

/**
 * Yönetimsel iptal (CLOS-233 `[170]` DEV-174 — CLOS-922 ortak çekirdek/CLOS-925 adapter).
 *
 * ⚠ `withdrawNegotiationSchema` DEĞİL: geri çekme yalnız önerenin kendi kararı ve gövde
 * almıyor; bu, sözleşme sahibinin workflow yönetimi kararı ve gerekçe taşıyabiliyor.
 */
export const cancelNegotiationSchema = z.strictObject({
  reason: z.string().trim().max(2000, "Gerekçe en fazla 2000 karakter olabilir").optional(),
});
export type CancelNegotiationInput = z.infer<typeof cancelNegotiationSchema>;

/**
 * ⚠ `cancelled` CLOS-233 `[170]` DEV-174 ile eklendi — `withdrawn`'DAN AYRI: `withdrawn`
 * yalnız ÖNERENİN kararı, `cancelled` sözleşme SAHİBİNİN workflow yönetimi kararı
 * (CLOS-922 ortak çekirdek/CLOS-925 adapter).
 */
export type NegotiationStatus =
  | "open"
  | "accepted"
  | "rejected"
  | "partially_decided"
  | "countered"
  | "withdrawn"
  | "resolved"
  | "cancelled";

/**
 * Müzakere durumlarının EKRAN METİNLERİ (CLOS-873).
 *
 * ⚠ ORTAK SÖZLÜK, çünkü aynı sunucu enum'u artık İKİ yüzeyde çiziliyor: sahip tarafı
 * (`ContractNegotiationSection`) ve katılımcı tarafı (`SharedNegotiationSection`). Metni
 * her iki bileşende ayrı yazsaydık aynı durum iki ekranda iki ad taşırdı ve enum'a yeni
 * bir değer eklendiğinde biri sessizce güncellenmeden kalırdı (kriter 6).
 *
 * ⚠ `Record<NegotiationStatus, string>` bilinçli: enum genişlerse TİP hatası veriyor,
 * yani yeni durum metnini yazmayı unutmak derlenmiyor. `Partial` ya da `?? durum` yazmak
 * o kapıyı kapatırdı ve ekranda ham enum değeri görünürdü.
 */
export const NEGOTIATION_STATUS_NAMES: Record<NegotiationStatus, string> = {
  open: "Karar bekliyor",
  accepted: "Kabul edildi",
  rejected: "Reddedildi",
  partially_decided: "Kısmen karara bağlandı",
  countered: "Karşı öneri verildi",
  withdrawn: "Geri çekildi",
  resolved: "Sonuçlandı",
  cancelled: "İptal edildi",
};

/** Öğe kararının ekran metni — aynı gerekçe, aynı tam kapsama kuralı. */
export const NEGOTIATION_DECISION_NAMES: Record<"pending" | "accepted" | "rejected", string> = {
  pending: "Bekliyor",
  accepted: "Kabul",
  rejected: "Ret",
};

export type NegotiationItemView = {
  id: string;
  targetClauseId: string;
  targetBlockIndex: number | null;
  currentText: string;
  proposedText: string;
  note: string | null;
  decision: "pending" | "accepted" | "rejected";
  decidedByName: string | null;
  decidedAt: string | null;
  decisionNote: string | null;
  appliedVersionId: string | null;
  /** Hedef maddenin GÜNCEL içeriği öneri anından farklıysa true — türetilmiş (ADR-0034). */
  targetChanged: boolean;
};

export type NegotiationView = {
  id: string;
  contractId: string;
  baseVersionId: string;
  proposerTenantId: string;
  proposerName: string;
  /** Öneren organizasyonun sözleşmedeki TARAFI (CLOS-1152) — bağ yoksa null, uydurulmaz. */
  actingPartyId: string | null;
  actingPartyName: string | null;
  parentNegotiationId: string | null;
  note: string | null;
  status: NegotiationStatus;
  /** Baz, sözleşmenin en yeni versiyonu değilse true — türetilmiş, yazılı durum değil. */
  staleBase: boolean;
  /** Bu seti KARARA bağlayacak taraf (öneren tarafın karşısı). */
  decidingTenantId: string;
  resolvedByName: string | null;
  resolvedAt: string | null;
  createdAt: string;
  items: NegotiationItemView[];
};

/**
 * ÖĞE ROZETİNİN METNİ — SET DURUMUNDAN TÜRETİLİYOR (CLOS-252 Tren-2C).
 *
 * ### ⚠ İKİNCİ KAYNAK ÖLDÜ
 *
 * Rozet yalnız `decision`'a bakıyordu. Canlı turda görüldü: set "Geri çekildi" iken karara
 * bağlanmamış öğe "Bekliyor" yazıyordu. İki cümle aynı anda doğru olamaz — kimse o öğeye
 * artık karar vermeyecek ve "Bekliyor" kullanıcıya VAR OLMAYAN bir iş gösteriyordu.
 *
 * ### ⚠ KARAR VERİLMİŞSE ÖĞE KENDİ KARARINI SÖYLER
 *
 * Set sonradan geri çekilse bile verilmiş bir kabul/ret TARİHTİR; onu set durumuyla ezmek
 * olan biteni silerdi. Türetme yalnız `pending` öğeye uygulanıyor.
 *
 * ### ⚠ SEBEP SETTE, ÖĞEDE DEĞİL
 *
 * İlk yazımda öğe rozeti kapanış sebebini de söylüyordu ("Geri çekildi") ve ekranda AYNI
 * kelime iki kez çıkıyordu: bir kez setin rozetinde, bir kez her öğe satırında (ölçüldü —
 * test "Found multiple elements" ile düştü). Daha kötüsü, öğe satırında okununca ÖĞENİN
 * geri çekildiği sanılıyordu; oysa geri çekilen SET.
 *
 * Öğe düzeyindeki gerçek tek cümledir: bu öğe karara bağlanmadı. Sebep setin kendi
 * rozetinde ve kapanış cümlesinde duruyor.
 *
 * ⚠ `Record<NegotiationStatus, string>`: enum genişlerse TİP hatası veriyor. `?? "Bekliyor"`
 * yazmak yeni bir durumu sessizce "bekliyor" gösterirdi — düzeltmeye çalıştığımız kusurun
 * ta kendisi.
 */
const OGE_BEKLEME_DURUMU: Record<NegotiationStatus, string> = {
  open: "Bekliyor",
  partially_decided: "Bekliyor",
  accepted: "Karara bağlanmadı",
  rejected: "Karara bağlanmadı",
  countered: "Karara bağlanmadı",
  withdrawn: "Karara bağlanmadı",
  resolved: "Karara bağlanmadı",
  cancelled: "Karara bağlanmadı",
};

export function negotiationItemStatusName(
  setStatus: NegotiationStatus,
  decision: "pending" | "accepted" | "rejected",
): string {
  if (decision !== "pending") return NEGOTIATION_DECISION_NAMES[decision];
  return OGE_BEKLEME_DURUMU[setStatus];
}

/** Bu setin cevapladığı öneri; kök öneride `null`. */
export function findParentNegotiation(
  setler: readonly NegotiationView[],
  set: NegotiationView,
): NegotiationView | null {
  if (!set.parentNegotiationId) return null;
  return setler.find((s) => s.id === set.parentNegotiationId) ?? null;
}

/**
 * BİR ÖĞENİN CEVAPLADIĞI METİN (CLOS-252 Tren-2C).
 *
 * ### ⚠ ZİNCİR YANLIŞ OKUNUYORDU
 *
 * Karşı öneri kartında üstü çizili metin öğenin `currentText`'iydi, yani BAZ VERSİYON
 * metni. Zincir "iki yıl → bir yıl → on sekiz ay" iken ekran "iki yıl → on sekiz ay"
 * gösteriyordu: cevaplanan öneri kayboluyordu (canlı turda görüldü).
 *
 * ### ⚠ `kind` DÖNÜYOR ÇÜNKÜ ETİKET DEĞİŞİYOR
 *
 * Sadece metin dönseydi ekran her hâlde aynı etiketi basardı. Parent bulunamadığında
 * (kapsam dışı, silinmiş halka) baz metne düşülüyor ve bu DURUM SÖYLENİYOR — kullanıcıya
 * "cevaplanan öneri buydu" demek yalan olurdu.
 *
 * ⚠ Eşleşme madde kimliğinden: parent aynı maddeyi taşımıyorsa cevaplanan bir metin de
 * yoktur. Sıraya ya da indekse güvenmek, farklı maddeleri karşılaştırırdı.
 */
export function answeredText(
  setler: readonly NegotiationView[],
  set: NegotiationView,
  oge: NegotiationItemView,
): { text: string; kind: "base" | "parent" } {
  const parent = findParentNegotiation(setler, set);
  const parentOge = parent?.items.find((i) => i.targetClauseId === oge.targetClauseId);
  if (parentOge) return { text: parentOge.proposedText, kind: "parent" };
  return { text: oge.currentText, kind: "base" };
}
